import { useState, useEffect, useCallback } from "react";
import { Modal } from "antd";
import {
  UserOutlined,
  ShopOutlined,
  CheckOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface ReportTypeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectPersonal: () => void;
  onSelectEstablishment: () => void;
}

const OPTIONS = ["personal", "establishment"] as const;
type OptionKey = (typeof OPTIONS)[number];

export const ReportTypeSelectionModal = ({
  open,
  onClose,
  onSelectPersonal,
  onSelectEstablishment,
}: ReportTypeSelectionModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [selected, setSelected] = useState<OptionKey>("personal");

  useEffect(() => {
    if (open) setSelected("personal");
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (selected === "personal") onSelectPersonal();
    else onSelectEstablishment();
  }, [selected, onSelectPersonal, onSelectEstablishment]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((prev) =>
          prev === "personal" ? "establishment" : "personal",
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleConfirm, onClose]);

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const bodyPad = isNarrow ? "16px" : "20px 24px";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{ background: R.surface, fontFamily: R.font, color: R.text }}
      >
        <ModalHeader
          icon={<FileTextOutlined />}
          title={t("reportType.selectType")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {OPTIONS.map((option) => (
            <OptionRow
              key={option}
              selected={selected === option}
              onClick={() => setSelected(option)}
              icon={
                option === "personal" ? <UserOutlined /> : <ShopOutlined />
              }
              label={
                option === "personal"
                  ? t("reportType.personal")
                  : t("reportType.establishment")
              }
            />
          ))}
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("common.select")}
            onClick={handleConfirm}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

const OptionRow = ({
  selected,
  onClick,
  icon,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "12px 14px",
      borderRadius: 12,
      border: `1px solid ${selected ? "rgba(217,119,6,0.32)" : R.border}`,
      background: selected ? R.brandTint : R.surface,
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "pointer",
      fontFamily: "inherit",
      textAlign: "left",
      transition: "background 120ms, border-color 120ms",
    }}
  >
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: selected ? R.brand : R.bg,
        color: selected ? "#FFFFFF" : R.textSoft,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 17,
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
    <span
      style={{
        flex: 1,
        fontSize: 14.5,
        fontWeight: 600,
        color: selected ? R.brandDeep : R.text,
      }}
    >
      {label}
    </span>
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: 10,
        background: selected ? R.brand : R.surface,
        border: `1px solid ${selected ? R.brand : R.borderMed}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        flexShrink: 0,
      }}
    >
      {selected && <CheckOutlined style={{ fontSize: 11 }} />}
    </span>
  </button>
);
